export type PlanId = 'free' | 'pro' | 'premium';

export interface PlanFeature {
  id: string;
  label: string;
  description: string;
  requiredPlan: PlanId;
}

export interface PlanInfo {
  id: PlanId;
  name: string;
  badge: string;
  storageGB: number;
  color: string;
  price: string;
}

export const planInfo: Record<PlanId, PlanInfo> = {
  free: {
    id: 'free',
    name: 'Free',
    badge: 'Starter',
    storageGB: 1,
    color: 'secondary',
    price: 'Gratis',
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    badge: 'Más popular',
    storageGB: 50,
    color: 'primary',
    price: '$29/mes',
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    badge: 'Profesional',
    storageGB: 500,
    color: 'accent',
    price: '$59/mes',
  },
};

export const planComparisonFeatures = [
  { label: 'Storage', free: '1 GB', pro: '50 GB', premium: '500 GB' },
  { label: 'Fotos', free: '—', pro: 'Ilimitadas', premium: 'Ilimitadas' },
  { label: 'Videos', free: '—', pro: '—', premium: 'Ilimitados' },
  { label: 'Lives', free: '—', pro: '—', premium: 'Hasta 10' },
  { label: 'Frecuencia', free: 'Automática', pro: 'Automática', premium: 'Personalizada' },
  { label: 'Analytics', free: 'Básico', pro: 'Básico', premium: 'Avanzado' },
  { label: 'Soporte', free: 'Comunidad', pro: 'Email', premium: '24/7 Prioritario' },
];

export const featureDefinitions: PlanFeature[] = [
  {
    id: 'fotos',
    label: 'Galería de Fotos',
    description: 'Subí y gestioná fotos de tu radio. Creá álbumes, destacá imágenes y compartí momentos con tu audiencia.',
    requiredPlan: 'pro',
  },
  {
    id: 'videos',
    label: 'Videos',
    description: 'Subí y gestioná videos. Publicá entrevistas, eventos grabados y contenido en video para tu comunidad.',
    requiredPlan: 'premium',
  },
  {
    id: 'lives',
    label: 'Transmisiones en Vivo',
    description: 'Creá y programá lives. Transmití en directo con YouTube, Facebook, VPS propio o Mux.',
    requiredPlan: 'premium',
  },
  {
    id: 'galerias',
    label: 'Galerías Multimedia',
    description: 'Creá galerías completas con fotos y videos para mostrar lo mejor de tu radio.',
    requiredPlan: 'pro',
  },
  {
    id: 'upload_fotos',
    label: 'Subida de Fotos',
    description: 'Subí fotos ilimitadas a tu galería. Compartí el detrás de escena de tu radio.',
    requiredPlan: 'pro',
  },
  {
    id: 'upload_videos',
    label: 'Subida de Videos',
    description: 'Subí videos en alta calidad. Entrevistas, eventos, conciertos y más.',
    requiredPlan: 'premium',
  },
  {
    id: 'storage_extendido',
    label: 'Almacenamiento Extendido',
    description: 'Más espacio para tus archivos. Sin preocuparte por límites de almacenamiento.',
    requiredPlan: 'pro',
  },
  {
    id: 'storage',
    label: 'Más Storage',
    description: 'Amplía tu capacidad de almacenamiento para fotos, videos y archivos de tu radio.',
    requiredPlan: 'pro',
  },
  {
    id: 'posts_con_imagenes',
    label: 'Posts con Imágenes',
    description: 'Creá publicaciones con imágenes y multimedia para mantener a tu audiencia conectada.',
    requiredPlan: 'pro',
  },
  {
    id: 'comunidad_completa',
    label: 'Comunidad Completa',
    description: 'Acceso total a la comunidad de oyentes. Foros, grupos y participación activa.',
    requiredPlan: 'pro',
  },
  {
    id: 'analytics_avanzado',
    label: 'Analytics Avanzado',
    description: 'Estadísticas detalladas de audiencia, engagement y rendimiento de tu contenido.',
    requiredPlan: 'premium',
  },
  {
    id: 'frecuencia_premium',
    label: 'Frecuencia Premium',
    description: 'Elegí una frecuencia personalizada para tu radio. Reservá números especiales y destacá en el dial.',
    requiredPlan: 'premium',
  },
  {
    id: 'soporte_prioritario',
    label: 'Soporte Prioritario',
    description: 'Atención prioritaria 24/7 para resolver cualquier problema técnico.',
    requiredPlan: 'premium',
  },
  {
    id: 'custom_domain',
    label: 'Dominio Propio',
    description: 'Vinculá tu propio dominio personalizado (ej: miradio.com) para tener presencia web profesional independiente.',
    requiredPlan: 'premium',
  },
];

export const currentPlanId: PlanId = 'free';

export const currentPlanStorageUsed = '0.2 GB';

export function getRequiredPlanBadge(requiredPlan: PlanId): string {
  switch (requiredPlan) {
    case 'pro':
      return 'Requiere Pro';
    case 'premium':
      return 'Requiere Premium';
    default:
      return '';
  }
}

export function getRequiredPlanColor(requiredPlan: PlanId): string {
  switch (requiredPlan) {
    case 'pro':
      return 'primary';
    case 'premium':
      return 'accent';
    default:
      return 'secondary';
  }
}